/**
 * CollectionsPage
 * 
 * Page displaying all available collections.
 */

import React from 'react';
import styled from '@emotion/styled';
import { FolderOpen } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useCollections } from '../lib/hooks/use-collections';
import { CollectionCard } from '../components/organisms/CollectionCard';
import { SearchBar } from '../components/organisms/SearchBar';
import { FunnelErrorBoundary, FunnelExperience } from '@template-project/storefront-funnel';
import { useFunnelAddToCart } from '../lib/hooks/use-funnel-add-to-cart';
import { useIntl } from '@/lib/intl';

const PageContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Header = styled.header`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #6b7280;
  margin: 0 0 24px 0;
`;

const SearchContainer = styled.div`
  margin-bottom: 32px;
`;

const CollectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;

const LoadingState = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;

const LoadingCard = styled.div`
  aspect-ratio: 4/3;
  background: #e5e7eb;
  border-radius: 16px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 48px;
  background: #fef2f2;
  border-radius: 12px;
  color: #991b1b;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  background: #f9fafb;
  border-radius: 16px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0;
`;

export function CollectionsPage() {
  const { collections, loading, error, pagination } = useCollections();
  const { countryConfig } = useIntl();
  const handleFunnelAddToCart = useFunnelAddToCart();
  const [searchParams] = useSearchParams();
  const urlParameters = Object.fromEntries(searchParams.entries());

  return (
    <PageContainer>
      <Header>
        <Title>Collections</Title>
        <Subtitle>Browse our curated product collections</Subtitle>
      </Header>

      <SearchContainer>
        <SearchBar placeholder="Search all products..." />
      </SearchContainer>

      {loading && (
        <LoadingState>
          {Array.from({ length: 6 }).map((_, i) => (
            <LoadingCard key={i} />
          ))}
        </LoadingState>
      )}

      {error && (
        <ErrorState>
          <p>Failed to load collections: {error}</p>
        </ErrorState>
      )}

      {!loading && !error && collections.length === 0 && (
        <EmptyState>
          <EmptyIcon><FolderOpen size={32} /></EmptyIcon>
          <EmptyTitle>No collections yet</EmptyTitle>
          <EmptyDescription>Check back soon for curated product collections.</EmptyDescription>
        </EmptyState>
      )}

      {!loading && !error && collections.length > 0 && (
        <CollectionsGrid>
          {collections.map((collection, index) => (
            <CollectionCard key={collection.id} collection={collection} index={index} />
          ))}
        </CollectionsGrid>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <p style={{ color: '#6b7280' }}>
            Showing {collections.length} of {pagination.total} collections
          </p>
        </div>
      )}

      <FunnelErrorBoundary>
        <FunnelExperience
          pageType="collection"
          context={{ url: '/collections', country: countryConfig.countryCode, urlParameters }}
          onAddToCart={handleFunnelAddToCart}
        />
      </FunnelErrorBoundary>
    </PageContainer>
  );
}

export default CollectionsPage;
